import { ChevronDown, ChevronUp } from 'lucide-react';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';

import { Botao } from '@/componentes/Botao';
import { Campo } from '@/componentes/Campo';
import { HistoriaNaMesa } from '@/historias/mesa';
import { interpretarNumero } from '@/lib/formato';
import { useSessao } from '@/modulos/sessao/consultas';

import { useRegistrarLeitura } from './consultas-frota';
import type { TipoDeMedidor } from './frota';
import { MensagemDeErro } from './MensagemDeErro';

/**
 * Registrar a leitura do medidor pela ficha (melhoria pedida pelo dono em
 * 24/09): a PRIMEIRA leitura de uma maquina que entrou sem ela, ou a CORRECAO.
 *
 * Nao existe "editar o horimetro". A leitura e so de insercao no banco (0007),
 * e a correcao e uma leitura nova com o motivo escrito — e a unica que pode ser
 * menor que a anterior. A frase de apoio diz isso, para ninguem procurar o
 * botao de apagar a leitura errada.
 *
 * So aparece para quem pode gerir o equipamento, como a faixa de consumo.
 */
export function FormularioLeitura({
  equipamentoId,
  tipoMedidor,
  temLeitura,
}: {
  readonly equipamentoId: string;
  readonly tipoMedidor: TipoDeMedidor;
  readonly temLeitura: boolean;
}) {
  const base = useId();
  const sessao = useSessao();
  const registrar = useRegistrarLeitura();
  const [valor, setValor] = useState('');
  const [motivo, setMotivo] = useState(temLeitura ? '' : 'Leitura inicial');
  /* UM ERRO POR CAMPO, e cada um aparece AO SAIR do campo (p.16; R01, p.28).
     Com um erro so, preso ao primeiro campo, a falta do motivo aparecia
     embaixo da leitura. */
  const [erroDoValor, setErroDoValor] = useState<string | null>(null);
  const [erroDoMotivo, setErroDoMotivo] = useState<string | null>(null);
  /* CORRIGIR E RARO, e fica atras do botao (pedido do dono, 31/10/2026): como
     o cupom e "Ajustar quem e onde" no lancamento de combustivel, o que nao se
     faz todo dia nao ocupa a ficha. A leitura INICIAL da maquina sem nenhuma
     e o contrario: e o que falta, e os campos ja vem abertos. */
  const [aberto, setAberto] = useState(!temLeitura);
  /* Quem abriu pelo botao continua no teclado: o foco vai para a leitura, e
     nao se perde com o botao que sai da tela. */
  const campoDoValor = useRef<HTMLInputElement>(null);
  const botaoDeCorrigir = useRef<HTMLButtonElement>(null);
  const trocouAgora = useRef(false);
  useEffect(() => {
    if (!trocouAgora.current) return;
    trocouAgora.current = false;
    (aberto ? campoDoValor.current : botaoDeCorrigir.current)?.focus();
  }, [aberto]);

  if (!(sessao.data?.permissoes ?? []).includes('equipamento.gerir')) return null;
  const unidade = tipoMedidor === 'odometro' ? 'km' : 'h';

  const conferirValor = (texto: string) => {
    const numero = interpretarNumero(texto);
    return numero === null || numero < 0 ? `Informe a leitura em ${unidade}, como 4.832 ou 4.832,5.` : null;
  };
  const conferirMotivo = (texto: string) =>
    texto.trim().length < 3 ? 'Escreva o motivo: leitura inicial, medidor trocado, painel zerado.' : null;

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const problemaNoValor = conferirValor(valor);
    const problemaNoMotivo = conferirMotivo(motivo);
    setErroDoValor(problemaNoValor);
    setErroDoMotivo(problemaNoMotivo);
    if (problemaNoValor || problemaNoMotivo) return;
    const numero = interpretarNumero(valor);
    if (numero === null) return;
    registrar.mutate(
      { id: equipamentoId, valor: numero, motivo: motivo.trim() },
      {
        onSuccess: () => {
          setValor('');
          setMotivo('');
          if (temLeitura) setAberto(false);
        },
      },
    );
  }

  if (temLeitura && !aberto) {
    return (
      <div className="flex border-t border-borda-sutil pt-3">
        {/* O botao cru (as classes do `Botao` fantasma pequeno) porque o foco
            volta a ele quando a correcao fecha, e o `Botao` nao repassa `ref`. */}
        <button
          ref={botaoDeCorrigir}
          type="button"
          className="botao botao-fantasma botao-p"
          aria-expanded={false}
          aria-controls={`${base}-correcao`}
          onClick={() => {
            trocouAgora.current = true;
            setAberto(true);
          }}
        >
          <ChevronDown aria-hidden="true" className="size-[var(--icone-controle)]" />
          Corrigir a leitura
        </button>
      </div>
    );
  }

  return (
    <form id={`${base}-correcao`} onSubmit={enviar} className="flex flex-col gap-2 border-t border-borda-sutil pt-3">
      {temLeitura ? (
        <div className="flex">
          <Botao
            nivel="fantasma"
            tamanho="p"
            aria-expanded
            aria-controls={`${base}-correcao`}
            onClick={() => {
              trocouAgora.current = true;
              setAberto(false);
              setErroDoValor(null);
              setErroDoMotivo(null);
            }}
          >
            <ChevronUp aria-hidden="true" className="size-[var(--icone-controle)]" />
            Fechar a correção
          </Botao>
        </div>
      ) : (
        <span className="txt-rotulo text-tinta-secundaria">Registrar a leitura inicial</span>
      )}
      <div className="flex flex-wrap items-end gap-4">
        <Campo id={`${base}-valor`} rotulo={`Leitura (${unidade})`} erro={erroDoValor}>
          <input
            ref={campoDoValor}
            id={`${base}-valor`}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            className="controle w-full"
            value={valor}
            onChange={(e) => {
              setErroDoValor(null);
              setValor(e.target.value);
            }}
            onBlur={() => {
              setErroDoValor(conferirValor(valor));
            }}
            {...(erroDoValor ? { 'aria-invalid': true, 'aria-describedby': `${base}-valor-erro` } : {})}
          />
        </Campo>
        <Campo id={`${base}-motivo`} rotulo="Motivo" erro={erroDoMotivo}>
          <input
            id={`${base}-motivo`}
            type="text"
            maxLength={200}
            autoComplete="off"
            className="controle w-full"
            value={motivo}
            onChange={(e) => {
              setErroDoMotivo(null);
              setMotivo(e.target.value);
            }}
            onBlur={() => {
              setErroDoMotivo(conferirMotivo(motivo));
            }}
            {...(erroDoMotivo ? { 'aria-invalid': true, 'aria-describedby': `${base}-motivo-erro` } : {})}
          />
        </Campo>
        <HistoriaNaMesa codigo="BTN-EQP-05" className="flex">
          <Botao nivel="secundario" type="submit" disabled={registrar.isPending}>
            Registrar leitura
          </Botao>
        </HistoriaNaMesa>
      </div>
      <span className="txt-apoio text-tinta-apagada">
        A leitura não se apaga. Corrigir registra uma nova, com o motivo, e ela passa a valer para a manutenção
        preventiva.
      </span>
      <MensagemDeErro erro={registrar.error} />
    </form>
  );
}
